import utils from '../utils'
import { RateLimitExceededError, StoreCardError, ProcessingError, APIError, GenericError } from '../errors'

const liveEndpoint = `https://rest.paymentsapi.io`
const testEndpoint = `https://sandbox.rest.paymentsapi.io`

const processResponse = (response) => {
  if (response.errors !== undefined) {
    switch (response.errors[0].category) {
    case 'ratelimit-exceeded':
      return Promise.reject(RateLimitExceededError(response.errors[0].message))
    default:
      return Promise.reject(new GenericError(response.errors[0].message))
    }
  }
  return Promise.resolve({
    receiptno: response.data.receipt.receiptno,
    status: response.data.receipt.status,
    statuscode: response.data.receipt.statuscode,
    statusmsg: response.data.receipt.statusmsg,
    payment_date: response.data.receipt.paymentDate,
    paymentamount: response.data.receipt.paymentAmount,
    txn: response.data.receipt.transaction.txnid,
    txnid: response.data.receipt.transaction.txnid,
    txndate: response.data.receipt.transaction.txndate,
    invtotal: response.data.receipt.transaction.total,
    transaction: response.data.receipt.transaction,
    completepage: response.data.receipt.completepage
  })
}

const mkQueryArgs = function(additionalOptions) {
  if (this.paymentTesting && typeof this.override_amount === 'number') {
    return {
      txnid: this.txn.txnid,
      override_amount: this.override_amount,
      ...additionalOptions
    }
  }
  else if (typeof this.amount === 'number') {
    return {
      txnid: this.txn.txnid,
      amount: this.amount,
      ...additionalOptions
    }
  }
  else {
    return {
      txnid: this.txn.txnid,
      ...additionalOptions
    }
  }
}

const paymentViaPayer = function(payer, additionalOptions) {
  const operation = (typeof additionalOptions.amount === 'number' && additionalOptions.amount > 0) ? 'makePaymentviaPayerIDwithAmount' : 'makePaymentviaPayerID'
  return this.graphQLMutate({
    opName: operation,
    variables: mkQueryArgs.call(this, { payerid: payer, ...additionalOptions })
  })
    .then(response => processResponse.call(this, response))
}

const paymentViaCard = function(cardDetails, additionalOptions) {
  return this.tokenizeCard(cardDetails)
    .then(cardToken => {
      const operation = (typeof this.amount === 'number' && this.amount > 0) ? 'makePaymentviaTokenwithAmount' : 'makePaymentviaToken'
      return this.graphQLMutate({
        opName: operation,
        variables: mkQueryArgs.call(this, { token: cardToken, ...additionalOptions })
      })
    })
    .then(response => processResponse.call(this, response))
}

const storeNewCard = function(cardDetails, additionalOptions) {
  return this.tokenizeCard(cardDetails)
    .then(cardToken => this.storeCreditCard(cardToken, additionalOptions))
}

function IntegrapayPayments(graphQLCLient, options) {
  this.graphQLQuery = (queryOptions) => graphQLCLient.then(client => client.query(queryOptions))
  this.graphQLMutate = (queryOptions) => graphQLCLient.then(client => client.mutate(queryOptions))
  this.paymentTesting = options.paymentTesting ? options.paymentTesting : false
  this.$store = options.store
  this.account = null
  this.txn = null
  this.txnOwnerCards = null
  this.businesskey = null
  this.__ignoreAuthUserCards = false
  this.__ignoreTxnUserCards = false
}

IntegrapayPayments.prototype.initPayment = function(txnid) {
  // Check if there is an already loaded txn in the VuexStore
  const loadTxn = () => {
    try {
      const response = this.$store.getters['getAdHocStorageValue'](`paymenttxn-${txnid}`)
      if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return Promise.resolve(response)
      }
    }
    catch (err) {
      // No-op
    }
    return this.graphQLQuery({
      opName: 'getTxn',
      variables: {
        txnid: txnid
      }
    })
  }

  return loadTxn()
    .then(response => {
      // Filter out the access denied errors for the credit card retreival as this may be disallowed
      // based on the permission on the logged in user account
      if (response.errors) {
        response.errors = response.errors.filter(err => {
          if (err.category === 'accessdenied') {
            if (!err.path.includes('creditcard') && !err.path.includes('account')) {
              return err
            }
          }
          else {
            return err
          }
        })
        if (response.errors && response.errors.length === 0) {
          delete response.errors
        }
      }

      if (!response.errors) {
        this.account = response.data.account
        this.txn = {
          txnid: response.data.txn.txnid,
          txndate: response.data.txn.txndate,
          txnuser: response.data.txn.owner,
          purchaser: response.data.txn.purchaser,
          payment_status: response.data.txn.payment ? response.data.txn.payment.status : null,
          amount: response.data.txn.total
        }
        try {
          this.person = response.data.account.person
          this.txnaccount = response.data.txn.account
          this.txnperson = response.data.txn.account ? response.data.txn.account.person : null
          this.txnCreditCards = response.data.txn.account ? response.data.txn.account.creditcard : []
        }
        catch (err) {
          this.person = null
          this.txnaccount = null
          this.txnperson = null
          this.txnCreditCards = []
        }
        this.businesskey = Promise.resolve(response.data.businesskey)
      }
      return response
    })
}

IntegrapayPayments.prototype.getTxn = function() {
  return this.txn
}

IntegrapayPayments.prototype.TxnID = function() {
  return this.txn.txnid
}

IntegrapayPayments.prototype.TxnAmount = function() {
  return this.txn.amount
}

IntegrapayPayments.prototype.txnOwner = function() {
  return this.txn.txnuser
}

IntegrapayPayments.prototype.authUser = function() {
  return this.account.id
}

IntegrapayPayments.prototype.isTxnOwner = function() {
  return (this.account.id === this.txn.txnuser)
}

IntegrapayPayments.prototype.isLoggedIn = function() {
  return this.account ? (this.account.id > 1) : false
}

IntegrapayPayments.prototype.getTxnOwnerStoreCreditCards = function() {
  if (this.__ignoreTxnUserCards) {
    return Promise.resolve({})
  }
  if (this.txn.txnuser && this.isLoggedIn() && this.txnOwner() !== this.authUser()) {
    this.txnOwnerCards = this.getStoredCreditCards(this.txnOwner())
      .then(cards => {
        return !cards ? [] : cards
      })
  }
  else {
    this.txnOwnerCards = Promise.resolve([])
  }
  return this.txnOwnerCards
}

IntegrapayPayments.prototype.getAuthUserStoreCreditCards = function() {
  if (this.__ignoreAuthUserCards) {
    return Promise.resolve({})
  }
  if (this.isLoggedIn() && Object.prototype.hasOwnProperty.call(this.account, 'creditcard') && this.account.creditcard) {
    this.authUserCards = [ this.account.creditcard ]
  }
  else {
    return Promise.resolve({})
  }
  return Promise.resolve(this.authUserCards[0])
}

IntegrapayPayments.prototype.getStoredCreditCards = function(id) {
  if (this.txnCreditCards) {
    return Promise.resolve(this.txnCreditCards)
  }
  return this.graphQLQuery({
    opName: 'getStoredCreditCards',
    variables: {
      user: id
    }
  })
    .then((response) => {
      if (response.errors) {
        throw new APIError(response.errors)
      }
      return response.data.account.creditcard
    })
    .catch(err => {
      throw new APIError('Error encountered retreiving Stored Credit Cards: ' + err.message)
    })
}

IntegrapayPayments.prototype.getIntegraypayAPIURI = function() {
  if (!this.businesskey) {
    this.businesskey = this.graphQLQuery({ opName: 'integrapayBusinessKey' })
      .then((response) => response.data.businesskey)
  }

  return this.businesskey
    .then(businesskey => {
      if (this.paymentTesting) {
        return `${utils.sanatizePath(testEndpoint)}/businesses/${businesskey}/services/tokenize-card`
      }
      return `${utils.sanatizePath(liveEndpoint)}/businesses/${businesskey}/services/tokenize-card`
    })
}

IntegrapayPayments.prototype.tokenizeCard = function(cardDetails) {
  if (this.cardToken) {
    return Promise.resolve(this.cardToken)
  }
  let data = {
    Card: {
      CardNumber: cardDetails.cardNumber.replace(/\s+/g, ''),
      CardholderName: cardDetails.cardholderName,
      ExpiryYear: cardDetails.expiryYear.toString(),
      ExpiryMonth: cardDetails.expiryMonth.padStart(2, '0'),
      Ccv: cardDetails.cvc.toString()
    }
  }

  return this.getIntegraypayAPIURI()
    .then(uri => fetch(uri, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }))
    .then(resp => {
      if (resp.status === 200) {
        return resp.json()
      }
      else {
        return resp.json()
          .then(resp => {
            throw new ProcessingError(`Tokenizing Payment Details: ${resp.errorCode} - ${resp.errorMessage}`)
          })
      }
    })
    .then(response => {
      this.cardToken = response.token
      return this.cardToken
    })
}

IntegrapayPayments.prototype.storeCreditCard = function(cardDetails, additionalOptions) {
  return this.tokenizeCard(cardDetails)
    .then(cardToken => {
      return this.graphQLMutate({
        opName: 'storeCreditCard',
        variables: {
          token: cardToken,
          authorization: Object.prototype.hasOwnProperty.call(additionalOptions, 'authorization') && additionalOptions.authorization !== null ? additionalOptions.authorization : null
        }
      })
    })
    .catch(err => {
      throw new APIError('Could not store Credit Card - ' + err.message)
    })
    .then(response => {
      if (response.errors) {
        throw new StoreCardError(null, response.errors)
      }
      this.payer = response.data.card
      this.payerid = response.data.card.payerid
      return this.payer
    })
}

IntegrapayPayments.prototype.removeCreditCard = function(userId) {
  return this.graphQLMutate({
    opName: 'removeCreditCard',
    variables: {
      user: userId
    }
  })
    .then(response => {
      if (response.errors) {
        throw new APIError(response.errors)
      }
    })
    .catch(err => {
      throw new APIError('Removing stored credit card: ' + err.message)
    })
}

IntegrapayPayments.prototype.processPayment = function(options) {
  if (this.paymentTesting && typeof options.override_amount === 'number') {
    this.override_amount = parseFloat(options.override_amount)
  }
  else if (typeof options.amount === 'number' && options.amount <= this.txn.amount) {
    this.amount = options.amount
  }
  if (options.selectedPayer) {
    return paymentViaPayer.call(this, options.selectedPayer, options.additionalOptions, options.additionalOptions)
  }
  else if (options.cardDetails.cardSave) {
    return storeNewCard.call(this, options.cardDetails, options.additionalOptions)
      .then(newPayer => {
        return Promise.all([ newPayer, paymentViaPayer.call(this, newPayer.payerid, options.additionalOptions) ])
      })
      .then(([newPayer, receipt]) => {
        return { ...receipt, ...{ payer: newPayer } }
      })
  }
  else {
    return paymentViaCard.call(this, options.cardDetails, options.additionalOptions)
  }
}

IntegrapayPayments.prototype.getAuthUserName = function() {
  try {
    if (this.person) {
      if (Object.prototype.hasOwnProperty.call(this.person, 'title') && this.person.title.length) {
        return this.person.title.length
      }
      else {
        return `${this.person.firstname} ${this.person.lastname}`
      }
    }
    else {
      return this.account.username
    }
  }
  catch (_err) {
    return 'Unknown/Anonymous'
  }
}

IntegrapayPayments.prototype.getTxnUserName = function() {
  try {
    if (this.txnperson) {
      if (Object.prototype.hasOwnProperty.call(this.txnperson, 'title') && this.txnperson.title.length) {
        return this.txnperson.title
      }
      else {
        return `${this.txnperson.firstname} ${this.txnperson.lastname}`
      }
    }
    else {
      return this.txnaccount.username
    }
  }
  catch (_err) {
    return 'Unknown/Anonymous'
  }
}
IntegrapayPayments.prototype.ignoreAuthUserCards = function(state) {
  this.__ignoreAuthUserCards = (state === undefined) ? false : state
}

IntegrapayPayments.prototype.ignoreTxnUserCards = function(state) {
  this.__ignoreTxnUserCards = (state === undefined) ? false : state
}

export default {
  methods: {
    integrapayClient: function(options) {
      const processor = new IntegrapayPayments(this.gqlClient, {
        store: this.$store,
        paymentTesting: this.config.paymentTesting,
        ...this.config.paymentProcessorOptions.integrapay
      })
      if (this.config.ignoreAuthUserCards !== undefined) {
        processor.ignoreAuthUserCards(this.config.ignoreAuthUserCards)
      }
      if (this.config.ignoreTxnUserCards !== undefined) {
        processor.ignoreTxnUserCards(this.config.ignoreTxnUserCards)
      }
      return processor
    }
  }
}
